<template>
  <v-content class="gb">
    <v-container fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" md="12" class="top-container">

          <!-- Follow Up Boss Connection Card -->
          <div class="crm-card">
            <div class="crm-card__header">
              <div>
                <h2 class="crm-card__title">Follow Up Boss</h2>
                <p class="crm-card__subtitle">Automatically sync Kiey clients to your Follow Up Boss CRM.</p>
              </div>
              <span :class="['crm-badge', fubConnected ? 'crm-badge--connected' : 'crm-badge--disconnected']">
                {{ fubConnected ? 'Connected' : 'Not Connected' }}
              </span>
            </div>

            <!-- Connected state -->
            <div v-if="fubConnected" class="crm-card__body">
              <div class="crm-stats">
                <div class="crm-stat">
                  <span class="crm-stat__label">Account</span>
                  <span class="crm-stat__value">{{ fubAccountName }}</span>
                </div>
                <div class="crm-stat" v-if="fubConnectedAt">
                  <span class="crm-stat__label">Connected</span>
                  <span class="crm-stat__value">{{ formatDate(fubConnectedAt) }}</span>
                </div>
                <div class="crm-stat">
                  <span class="crm-stat__label">Clients Synced</span>
                  <span class="crm-stat__value">{{ fubPushCount }}</span>
                </div>
                <div class="crm-stat" v-if="fubLastPushAt">
                  <span class="crm-stat__label">Last Sync</span>
                  <span class="crm-stat__value">{{ formatDate(fubLastPushAt) }}</span>
                </div>
              </div>
              <button class="crm-btn crm-btn--disconnect" :disabled="fubLoading" @click="disconnectFub">
                {{ fubLoading ? 'Disconnecting...' : 'Disconnect' }}
              </button>
            </div>

            <!-- Disconnected state -->
            <div v-else class="crm-card__body">
              <div class="crm-connect-form">
                <div class="crm-input-group">
                  <div class="crm-input-wrap">
                    <input
                        id="fub-key"
                        v-model="fubApiKey"
                        :type="showFubKey ? 'text' : 'password'"
                        class="crm-input"
                        placeholder="Enter your API key from Admin > API in Follow Up Boss"
                    />
                    <button class="crm-input-toggle" @click="showFubKey = !showFubKey" type="button">
                      {{ showFubKey ? 'Hide' : 'Show' }}
                    </button>
                  </div>
                  <span v-if="fubError" class="crm-input-error">{{ fubError }}</span>
                </div>
                <button
                    class="crm-btn crm-btn--connect"
                    :disabled="!fubApiKey || fubLoading"
                    @click="connectFub"
                >
                  {{ fubLoading ? 'Connecting...' : 'Connect' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Sierra Interactive Connection Card -->
          <div class="crm-card" style="margin-top: 24px;">
            <div class="crm-card__header">
              <div>
                <h2 class="crm-card__title">Sierra Interactive</h2>
                <p class="crm-card__subtitle">Automatically create Sierra Interactive leads from your Kiey clients and referrals.</p>
              </div>
              <span :class="['crm-badge', sierraConnected ? 'crm-badge--connected' : 'crm-badge--disconnected']">
                {{ sierraConnected ? 'Connected' : 'Not Connected' }}
              </span>
            </div>

            <!-- Connected state -->
            <div v-if="sierraConnected" class="crm-card__body">
              <div class="crm-stats">
                <div class="crm-stat">
                  <span class="crm-stat__label">Account</span>
                  <span class="crm-stat__value">{{ sierraAccountName }}</span>
                </div>
                <div class="crm-stat" v-if="sierraConnectedAt">
                  <span class="crm-stat__label">Connected</span>
                  <span class="crm-stat__value">{{ formatDate(sierraConnectedAt) }}</span>
                </div>
                <div class="crm-stat">
                  <span class="crm-stat__label">Leads Created</span>
                  <span class="crm-stat__value">{{ sierraPushCount }}</span>
                </div>
                <div class="crm-stat" v-if="sierraLastPushAt">
                  <span class="crm-stat__label">Last Sync</span>
                  <span class="crm-stat__value">{{ formatDate(sierraLastPushAt) }}</span>
                </div>
                <div class="crm-stat">
                  <span class="crm-stat__label">New Leads Are</span>
                  <select
                      class="crm-select"
                      :disabled="sierraLoading"
                      :value="sierraLeadType"
                      @change="updateSierraLeadType($event.target.value)"
                  >
                    <option v-for="option in sierraLeadTypes" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>
              <button class="crm-btn crm-btn--disconnect" :disabled="sierraLoading" @click="disconnectSierra">
                {{ sierraLoading ? 'Disconnecting...' : 'Disconnect' }}
              </button>
            </div>

            <!-- Disconnected state -->
            <div v-else class="crm-card__body">
              <div class="crm-connect-form">
                <div class="crm-input-group">
                  <div class="crm-input-wrap">
                    <input
                        id="sierra-key"
                        v-model="sierraApiKey"
                        :type="showSierraKey ? 'text' : 'password'"
                        class="crm-input"
                        placeholder="Enter your API key from Settings > API in Sierra Interactive"
                    />
                    <button class="crm-input-toggle" @click="showSierraKey = !showSierraKey" type="button">
                      {{ showSierraKey ? 'Hide' : 'Show' }}
                    </button>
                  </div>
                  <span v-if="sierraError" class="crm-input-error">{{ sierraError }}</span>
                </div>
                <div class="crm-input-group crm-input-group--narrow">
                  <label class="crm-input-label" for="sierra-lead-type">New leads are</label>
                  <select id="sierra-lead-type" class="crm-select crm-select--boxed" v-model="sierraLeadType">
                    <option v-for="option in sierraLeadTypes" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </div>
                <button
                    class="crm-btn crm-btn--connect"
                    :disabled="!sierraApiKey || sierraLoading"
                    @click="connectSierra"
                >
                  {{ sierraLoading ? 'Connecting...' : 'Connect' }}
                </button>
              </div>
            </div>

            <p class="crm-card__note">
              Sierra Interactive requires an email address to create a lead. Clients who signed up with a phone number
              only are listed below as <strong>Skipped</strong> — add their email in Kiey and they'll sync on their next
              referral.
            </p>
          </div>

          <!-- Sync History Table -->
          <div class="crm-card" style="margin-top: 24px;">
            <div class="crm-card__header">
              <h2 class="crm-card__title">Sync History</h2>
              <button class="crm-btn-icon" :disabled="syncLogLoading" @click="loadSyncLog" title="Refresh">
                <i class="fa fa-sync" :class="{ 'fa-spin': syncLogLoading }"></i>
              </button>
            </div>

            <div class="crm-table-wrap">
              <table class="crm-table" v-if="syncLog.length > 0">
                <thead>
                  <tr>
                    <th>CRM</th>
                    <th>Client Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Service</th>
                    <th>Status</th>
                    <th>Synced</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in syncLog" :key="item.crm + '-' + item.id">
                    <td>{{ item.crm_label }}</td>
                    <td>{{ item.client_name }}</td>
                    <td>{{ item.client_phone }}</td>
                    <td>{{ item.client_email }}</td>
                    <td>{{ item.event_type === 'Registration' ? 'New Client' : 'Referral' }}</td>
                    <td>{{ item.service_category || '—' }}</td>
                    <td>
                      <span :class="['crm-status-pill', 'crm-status-pill--' + statusClass(item.status)]" :title="item.error || ''">
                        {{ statusLabel(item.status) }}
                      </span>
                    </td>
                    <td>{{ formatDate(item.synced_at) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="crm-table-empty">
                <p>No clients have been synced yet.</p>
              </div>
            </div>
          </div>

          <!-- Coming Soon -->
          <div class="crm-card crm-card--muted" style="margin-top: 24px;">
            <div class="crm-card__header">
              <div>
                <h2 class="crm-card__title" style="color: #717e91;">More CRMs Coming Soon</h2>
                <p class="crm-card__subtitle">KVCore, Chime, and more.</p>
              </div>
            </div>
          </div>

        </v-col>
      </v-row>
    </v-container>
  </v-content>
</template>

<script>
import axios from "axios";
import config from "@/config/constants.js";
import { mapState } from "vuex";
import { firestore } from "@/config/firebase";
import moment from "moment";

// Sierra's leadType: 1 -> Buyer, 2 -> Seller, 3 -> Both.
const SIERRA_LEAD_TYPES = [
  { value: 3, label: 'Buyer & Seller' },
  { value: 1, label: 'Buyer' },
  { value: 2, label: 'Seller' },
];

export default {
  computed: {
    ...mapState({
      user: state => state.Authenticate.user,
    }),
  },
  created() {
    this.loadCrmStatus();
    this.loadSyncLog();
  },
  data() {
    return {
      // Follow Up Boss
      fubApiKey: '',
      fubConnected: false,
      fubAccountName: '',
      fubConnectedAt: null,
      fubPushCount: 0,
      fubLastPushAt: null,
      fubLoading: false,
      fubError: '',
      showFubKey: false,
      // Sierra Interactive
      sierraApiKey: '',
      sierraConnected: false,
      sierraAccountName: '',
      sierraConnectedAt: null,
      sierraPushCount: 0,
      sierraLastPushAt: null,
      sierraLeadType: 3,
      sierraLoading: false,
      sierraError: '',
      showSierraKey: false,
      sierraLeadTypes: SIERRA_LEAD_TYPES,
      // Shared sync history (both CRMs, newest first)
      syncLog: [],
      syncLogLoading: false,
    };
  },
  methods: {
    formatDate(date) {
      const millis = this.toMillis(date);
      if (!millis) return '';
      return moment(millis).format('MMM D, YYYY h:mm A');
    },
    // Timestamps reach this page in three shapes: a live Firestore SDK
    // Timestamp (`seconds`) from the tenant doc read, the JSON the API
    // serializes a server Timestamp into (`_seconds`), and a plain Date set
    // optimistically right after connecting.
    toMillis(date) {
      if (!date) return 0;
      if (typeof date.seconds === 'number') return date.seconds * 1000;
      if (typeof date._seconds === 'number') return date._seconds * 1000;
      const parsed = new Date(date).getTime();
      return isNaN(parsed) ? 0 : parsed;
    },
    statusClass(status) {
      if (status === 'success') return 'success';
      if (status === 'skipped') return 'skipped';
      return 'failed';
    },
    statusLabel(status) {
      if (status === 'success') return 'Synced';
      if (status === 'skipped') return 'Skipped';
      return 'Failed';
    },
    // Both CRMs store their connection state on the tenant doc, so one read
    // hydrates both cards.
    async loadCrmStatus() {
      const tenantId = this.user.tenant_current_logged || this.user.tenant_id;
      if (!tenantId) return;
      try {
        const doc = await firestore.collection('tenants').doc(tenantId).get();
        if (!doc.exists) return;
        const data = doc.data();

        if (data.fub_connected) {
          this.fubConnected = true;
          this.fubAccountName = data.fub_account_name || '';
          this.fubConnectedAt = data.fub_connected_at || null;
          this.fubPushCount = data.fub_push_count || 0;
          this.fubLastPushAt = data.fub_last_push_at || null;
        }

        if (data.sierra_connected) {
          this.sierraConnected = true;
          this.sierraAccountName = data.sierra_account_name || '';
          this.sierraConnectedAt = data.sierra_connected_at || null;
          this.sierraPushCount = data.sierra_push_count || 0;
          this.sierraLastPushAt = data.sierra_last_push_at || null;
        }
        if (data.sierra_lead_type) {
          this.sierraLeadType = Number(data.sierra_lead_type);
        }
      } catch (e) {
        console.error('Failed to load CRM status:', e);
      }
    },
    async connectFub() {
      this.fubError = '';
      this.fubLoading = true;
      try {
        const response = await axios.put(config.api_url + 'fub/connect', {
          fub_api_key: this.fubApiKey,
        }, {
          headers: { Authorization: localStorage.getItem('auth_token') },
        });
        if (response.data.code === 'SUCCESS') {
          this.fubConnected = true;
          this.fubAccountName = response.data.fub_account_name || '';
          this.fubApiKey = '';
          this.fubConnectedAt = new Date();
          this.fubPushCount = 0;
          this.loadSyncLog();
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.error;
        this.fubError = msg || 'Failed to connect. Please check your API key.';
      } finally {
        this.fubLoading = false;
      }
    },
    async disconnectFub() {
      this.fubLoading = true;
      try {
        await axios.put(config.api_url + 'fub/connect', {
          fub_api_key: '',
        }, {
          headers: { Authorization: localStorage.getItem('auth_token') },
        });
        this.fubConnected = false;
        this.fubAccountName = '';
        this.fubApiKey = '';
        this.fubConnectedAt = null;
        this.fubPushCount = 0;
        this.fubLastPushAt = null;
        this.loadSyncLog();
      } catch (error) {
        console.error('Failed to disconnect FUB:', error);
      } finally {
        this.fubLoading = false;
      }
    },
    async connectSierra() {
      this.sierraError = '';
      this.sierraLoading = true;
      try {
        const response = await axios.put(config.api_url + 'sierra/connect', {
          sierra_api_key: this.sierraApiKey,
          sierra_lead_type: this.sierraLeadType,
        }, {
          headers: { Authorization: localStorage.getItem('auth_token') },
        });
        if (response.data.code === 'SUCCESS') {
          this.sierraConnected = true;
          this.sierraAccountName = response.data.sierra_account_name || '';
          this.sierraLeadType = Number(response.data.sierra_lead_type) || this.sierraLeadType;
          this.sierraApiKey = '';
          this.sierraConnectedAt = new Date();
          this.sierraPushCount = 0;
          this.loadSyncLog();
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.error;
        this.sierraError = msg || 'Failed to connect. Please check your API key.';
      } finally {
        this.sierraLoading = false;
      }
    },
    async disconnectSierra() {
      this.sierraLoading = true;
      try {
        await axios.put(config.api_url + 'sierra/connect', {
          sierra_api_key: '',
        }, {
          headers: { Authorization: localStorage.getItem('auth_token') },
        });
        this.sierraConnected = false;
        this.sierraAccountName = '';
        this.sierraApiKey = '';
        this.sierraConnectedAt = null;
        this.sierraPushCount = 0;
        this.sierraLastPushAt = null;
        this.loadSyncLog();
      } catch (error) {
        console.error('Failed to disconnect Sierra:', error);
      } finally {
        this.sierraLoading = false;
      }
    },
    async updateSierraLeadType(value) {
      const leadType = Number(value);
      const previous = this.sierraLeadType;
      this.sierraLeadType = leadType;
      this.sierraLoading = true;
      try {
        await axios.put(config.api_url + 'sierra/connect', {
          sierra_lead_type: leadType,
        }, {
          headers: { Authorization: localStorage.getItem('auth_token') },
        });
      } catch (error) {
        console.error('Failed to update Sierra lead type:', error);
        this.sierraLeadType = previous;
      } finally {
        this.sierraLoading = false;
      }
    },
    // Pulls both CRM logs and merges them into one newest-first table so the
    // agent sees every sync attempt in one place.
    async loadSyncLog() {
      this.syncLogLoading = true;
      const headers = { Authorization: localStorage.getItem('auth_token') };
      try {
        const [fub, sierra] = await Promise.all([
          axios.get(config.api_url + 'fub/sync-log', { headers }).catch(error => {
            console.error('Failed to load Follow Up Boss sync log:', error);
            return null;
          }),
          axios.get(config.api_url + 'sierra/sync-log', { headers }).catch(error => {
            console.error('Failed to load Sierra Interactive sync log:', error);
            return null;
          }),
        ]);

        const rows = []
          .concat(this.tagRows(fub, 'fub', 'Follow Up Boss'))
          .concat(this.tagRows(sierra, 'sierra', 'Sierra Interactive'));

        rows.sort((a, b) => this.toMillis(b.synced_at) - this.toMillis(a.synced_at));
        this.syncLog = rows;
      } finally {
        this.syncLogLoading = false;
      }
    },
    tagRows(response, crm, crmLabel) {
      if (!response || !response.data || response.data.code !== 'SUCCESS') return [];
      return (response.data.data || []).map(row => Object.assign({}, row, { crm, crm_label: crmLabel }));
    },
  },
};
</script>

<style lang="scss" scoped>
.crm-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: 32px 40px;
  max-width: 1222px;
  margin: 0 auto 16px;

  &--muted {
    opacity: 0.5;
  }
}

.crm-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}

.crm-card__title {
  color: #041332;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
}

.crm-card__subtitle {
  color: #717e91;
  font-size: 14px;
  margin: 6px 0 0;
  line-height: 1.4;
}

.crm-card__note {
  color: #717e91;
  font-size: 12px;
  line-height: 1.5;
  margin: 20px 0 0;

  strong {
    color: #041332;
    font-weight: 600;
  }
}

.crm-card__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

// Badge
.crm-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;

  &--connected {
    background: #E8F5E9;
    color: #2E7D32;
  }

  &--disconnected {
    background: #FFF3E0;
    color: #E65100;
  }
}

// Stats row (connected state)
.crm-stats {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.crm-stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.crm-stat__label {
  color: #717e91;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.crm-stat__value {
  color: #041332;
  font-size: 15px;
  font-weight: 500;
}

// Connect form (disconnected state)
.crm-connect-form {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  width: 100%;
}

.crm-input-group {
  flex: 1;

  &--narrow {
    flex: 0 0 auto;
  }
}

.crm-input-label {
  display: block;
  color: #041332;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
}

.crm-input-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #29ABE2;
  }
}

.crm-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 14px;
  font-size: 14px;
  color: #041332;
  background: transparent;

  &::placeholder {
    color: #9ca3af;
  }
}

.crm-input-toggle {
  border: none;
  background: none;
  color: #29ABE2;
  font-size: 13px;
  font-weight: 500;
  padding: 10px 14px;
  cursor: pointer;

  &:hover {
    color: #1a8bc7;
  }
}

.crm-input-error {
  display: block;
  color: #E53935;
  font-size: 12px;
  margin-top: 6px;
}

.crm-select {
  border: none;
  background: transparent;
  outline: none;
  color: #041332;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--boxed {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 14px;
    background: #fff;

    &:focus {
      border-color: #29ABE2;
    }
  }
}

// Buttons
.crm-btn {
  border: none;
  border-radius: 8px;
  padding: 10px 28px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--connect {
    background: #29ABE2;
    color: #fff;

    &:hover:not(:disabled) {
      background: #1a8bc7;
    }
  }

  &--disconnect {
    background: transparent;
    color: #E53935;
    border: 1px solid #E53935;
    padding: 9px 28px;

    &:hover:not(:disabled) {
      background: #FFEBEE;
    }
  }
}

.crm-btn-icon {
  border: none;
  background: none;
  color: #717e91;
  font-size: 14px;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;

  &:hover {
    color: #041332;
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Table
.crm-table-wrap {
  margin-top: -8px;
}

.crm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    color: #717e91;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;
  }

  td {
    padding: 12px 16px;
    color: #041332;
    border-bottom: 1px solid #f3f4f6;
  }

  tbody tr:hover {
    background: #f9fafb;
  }
}

.crm-status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  &--success {
    background: #E8F5E9;
    color: #2E7D32;
  }

  &--failed {
    background: #FFEBEE;
    color: #C62828;
  }

  &--skipped {
    background: #F3F4F6;
    color: #717e91;
  }
}

.crm-table-empty {
  text-align: center;
  padding: 40px 0;
  color: #717e91;
  font-size: 14px;
}

// Responsive
@media (max-width: 768px) {
  .crm-card {
    padding: 20px 16px;
  }

  .crm-card__header {
    flex-direction: column;
    gap: 12px;
  }

  .crm-card__body {
    flex-direction: column;
    align-items: stretch;
  }

  .crm-connect-form {
    flex-direction: column;
    align-items: stretch;
  }

  .crm-stats {
    gap: 16px;
  }

  .crm-table-wrap {
    overflow-x: auto;
  }

  .crm-table {
    min-width: 720px;
  }
}

@media (max-width: 480px) {
  .crm-card {
    padding: 16px 12px;
  }

  .crm-card__title {
    font-size: 20px;
  }
}
</style>
